import React from 'react';

/**
 * The console used emoji as its only iconography ("🚦 TRAFFIC DRIVERS").
 * Emoji render at the mercy of the platform font, cannot be recoloured to
 * match state, and are announced literally by screen readers — VoiceOver
 * reads the traffic-drivers tab as "vertical traffic light, TRAFFIC DRIVERS".
 * These are stroke icons on a 24-unit grid instead: they inherit `currentColor`
 * so active/inactive states are one class change, and they are `aria-hidden`
 * because in every use here the adjacent text is the accessible name.
 */
export type IconName =
  | 'growth'
  | 'sales'
  | 'devops'
  | 'products'
  | 'instagram'
  | 'overview'
  | 'affiliates'
  | 'qr'
  | 'analytics'
  | 'orders'
  | 'transactions'
  | 'customers'
  | 'refunds'
  | 'build'
  | 'coverage'
  | 'quality'
  | 'security'
  | 'system'
  | 'deploy'
  | 'content'
  | 'campaign'
  | 'settings'
  | 'pricing'
  | 'search'
  | 'calendar'
  | 'chevronDown'
  | 'chevronRight'
  | 'menu'
  | 'close'
  | 'logout'
  | 'refresh'
  | 'arrowUp'
  | 'arrowDown'
  | 'dash'
  | 'alert'
  | 'inbox'
  | 'collapse'
  | 'expand'
  | 'external'
  | 'shipping'
  | 'referrals';

const PATHS: Record<IconName, React.ReactNode> = {
  growth: <><path d="M3 17l6-6 4 4 7-7" /><path d="M14 8h6v6" /></>,
  sales: <><path d="M3 6h18v12H3z" /><path d="M3 10h18" /><circle cx="8" cy="14" r="1.4" /></>,
  devops: <><rect x="3" y="4" width="18" height="7" rx="2" /><rect x="3" y="13" width="18" height="7" rx="2" /><path d="M7 7.5h.01M7 16.5h.01" /></>,
  products: <><path d="M21 8l-9-5-9 5 9 5 9-5z" /><path d="M3 8v8l9 5 9-5V8" /><path d="M12 13v8" /></>,
  instagram: <><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><path d="M17 7h.01" /></>,
  overview: <><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></>,
  affiliates: <><circle cx="9" cy="8" r="3" /><path d="M3 20a6 6 0 0112 0" /><path d="M16 6a3 3 0 010 6" /><path d="M18 20a5.5 5.5 0 00-2-4.3" /></>,
  qr: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><path d="M14 14h3v3h-3zM20 14v3M14 20h6" /></>,
  analytics: <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>,
  orders: <><path d="M5 7h14l-1 13H6L5 7z" /><path d="M9 7V5a3 3 0 016 0v2" /></>,
  // A van: fulfilment is a different job from the order record itself.
  shipping: <><path d="M3 7h11v9H3z" /><path d="M14 10h4l3 3v3h-7z" /><circle cx="7" cy="18" r="1.6" /><circle cx="17" cy="18" r="1.6" /></>,
  // One node introducing two others — a referral, not a customer list.
  referrals: <><circle cx="6" cy="12" r="2.5" /><circle cx="18" cy="6" r="2.5" /><circle cx="18" cy="18" r="2.5" /><path d="M8.2 10.8l7.5-3.6M8.2 13.2l7.5 3.6" /></>,
  transactions: <><path d="M4 8h13l-3-3" /><path d="M20 16H7l3 3" /></>,
  customers: <><circle cx="12" cy="8" r="3.5" /><path d="M5 20a7 7 0 0114 0" /></>,
  refunds: <><path d="M4 10h10a5 5 0 010 10h-4" /><path d="M8 6l-4 4 4 4" /></>,
  build: <><path d="M14.5 5.5a4 4 0 00-5.4 5.4L4 16v4h4l5.1-5.1a4 4 0 005.4-5.4l-2.6 2.6-2.4-2.4 2.6-2.6z" /></>,
  coverage: <><path d="M9 11l2.5 2.5L16 9" /><rect x="4" y="3" width="16" height="18" rx="2" /></>,
  quality: <><path d="M12 3l2.6 5.6 6 .8-4.4 4.2 1.1 6.1L12 16.8 6.7 19.7l1.1-6.1L3.4 9.4l6-.8L12 3z" /></>,
  security: <><path d="M12 3l8 3v6c0 4.4-3.2 7.9-8 9-4.8-1.1-8-4.6-8-9V6l8-3z" /><path d="M9.5 12l1.8 1.8 3.4-3.6" /></>,
  system: <><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4" /></>,
  deploy: <><path d="M12 3c3.5 2 5.5 5.5 5.5 9L12 17l-5.5-5c0-3.5 2-7 5.5-9z" /><circle cx="12" cy="10" r="1.6" /><path d="M8.5 17.5L7 21l3.5-1.5M15.5 17.5L17 21l-3.5-1.5" /></>,
  content: <><path d="M4 5h16v14H4z" /><path d="M4 15l4.5-4.5 3 3L15 10l5 5" /><circle cx="9" cy="8.5" r="1.3" /></>,
  campaign: <><path d="M4 10v4h3l7 4V6l-7 4H4z" /><path d="M18 9a4 4 0 010 6" /></>,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 00.3 1.9l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-2.9 1.2v.2a2 2 0 11-4 0v-.1A1.7 1.7 0 007 19.4l-.1.1a2 2 0 11-2.8-2.8l.1-.1A1.7 1.7 0 002.4 14H2a2 2 0 110-4h.2A1.7 1.7 0 004.6 7l-.1-.1a2 2 0 112.8-2.8l.1.1A1.7 1.7 0 0010 4.6V4a2 2 0 114 0v.2a1.7 1.7 0 002.9 1.2l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 001.2 2.9H22a2 2 0 110 4h-.2a1.7 1.7 0 00-1.6 1z" /></>,
  pricing: <><path d="M12 4v16M8.5 8.5A2.5 2.5 0 0111 6h3a2.5 2.5 0 010 5h-4a2.5 2.5 0 000 5h3.5a2.5 2.5 0 002.2-1.3" /></>,
  search: <><circle cx="11" cy="11" r="6.5" /><path d="M20 20l-4.2-4.2" /></>,
  calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></>,
  chevronDown: <path d="M6 9l6 6 6-6" />,
  chevronRight: <path d="M9 6l6 6-6 6" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  logout: <><path d="M15 4h3a2 2 0 012 2v12a2 2 0 01-2 2h-3" /><path d="M10 8l-4 4 4 4M6 12h10" /></>,
  refresh: <><path d="M20 11a8 8 0 10-1.6 5.9" /><path d="M20 5v6h-6" /></>,
  arrowUp: <path d="M12 19V5M6 11l6-6 6 6" />,
  arrowDown: <path d="M12 5v14M6 13l6 6 6-6" />,
  dash: <path d="M6 12h12" />,
  alert: <><path d="M12 4l9 16H3l9-16z" /><path d="M12 10v4M12 17h.01" /></>,
  inbox: <><path d="M3 13h5l1.5 3h5L16 13h5" /><path d="M3 13l3-8h12l3 8v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5z" /></>,
  collapse: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M10 4v16" /><path d="M7 10l-2 2 2 2" /></>,
  expand: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M10 4v16" /><path d="M5 10l2 2-2 2" /></>,
  external: <><path d="M14 5h5v5" /><path d="M19 5l-8 8" /><path d="M18 14v4a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h4" /></>,
};

interface IconProps {
  name: IconName;
  className?: string;
  /**
   * Only pass this when the icon is the *only* content of a control. Anywhere
   * an adjacent text label exists the icon must stay hidden from the
   * accessibility tree so the label is not read twice.
   */
  title?: string;
  strokeWidth?: number;
}

const Icon: React.FC<IconProps> = ({ name, className = 'h-5 w-5', title, strokeWidth = 1.75 }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden={title ? undefined : true}
    role={title ? 'img' : undefined}
    focusable="false"
  >
    {title ? <title>{title}</title> : null}
    {PATHS[name]}
  </svg>
);

export default Icon;
